(function () {
  if (window.UserAllianceAuth) return;

  const STORAGE_KEY = 'userAlliance.auth';
  const LOGIN_ENDPOINT = 'https://test-api.engweapp.cn/api/shopify/alliance/auth/login';
  const emptySession = () => ({ storeCode: '', email: '', accessToken: '', affiliateId: '' });
  const normaliseSession = (value) => {
    if (typeof value?.accessToken !== 'string' || !value.accessToken.trim() || typeof value?.email !== 'string' || !value.email.trim()) return emptySession();
    const rawStoreCode = typeof value?.storeCode === 'string' ? value.storeCode.trim().toUpperCase() : '';
    if (!rawStoreCode) return emptySession();
    const affiliateId = value?.affiliateId === undefined || value?.affiliateId === null ? '' : value.affiliateId;
    return { storeCode: rawStoreCode === 'GB' ? 'UK' : rawStoreCode, email: value.email.trim(), accessToken: value.accessToken.trim(), affiliateId };
  };
  let session;
  try {
    session = normaliseSession(JSON.parse(localStorage.getItem(STORAGE_KEY)));
  } catch (error) {
    session = emptySession();
  }
  let activeDialog;
  let sessionRevision = 0;
  const captchaLoaders = new Map();
  const getSession = () => ({ ...session });
  const isAuthenticated = () => Boolean(session.accessToken && session.email && session.storeCode);
  const syncSession = (value) => {
    session = normaliseSession(value);
    sessionRevision += 1;
    if (!session.accessToken) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (error) { /* Ignore unavailable storage. */ }
    }
    window.userAllianceAccessToken = session.accessToken;
    window.userAllianceEmail = session.email;
    window.userAllianceStoreCode = session.storeCode;
    window.userAllianceAffiliateId = session.affiliateId;
    document.documentElement.classList.toggle('userAlliance-authenticated', isAuthenticated());
    if (!isAuthenticated()) {
      const template = document.querySelector('[data-ua-auth-primary]') || document.querySelector('[data-ua-auth-template]');
      if (template) console.log(template.dataset.storeCode, '*****************storeCode');
    }
    document.dispatchEvent(new CustomEvent('user-alliance:auth-change', { detail: getSession() }));
  };
  try {
    if (session.accessToken) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (error) { /* Ignore unavailable storage. */ }
  const saveSession = (value) => {
    const next = normaliseSession(value);
    try {
      if (next.accessToken) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Keep the current tab usable when browser storage is unavailable.
    }
    syncSession(next);
  };

  // Only alliance API requests use this wrapper; signed file downloads do not.
  const request = async (url, options = {}) => {
    const revision = sessionRevision;
    const response = await fetch(url, options);
    options.signal?.throwIfAborted();
    let authFailed = response.status === 401 || response.status === 403;
    if (!authFailed) {
      try {
        const payload = await response.clone().json();
        authFailed = [401, 403].includes(Number(payload?.code));
      } catch (error) { /* Let callers handle non-JSON responses. */ }
    }
    options.signal?.throwIfAborted();
    // A response belonging to an old login must never clear a newer session.
    if (revision !== sessionRevision) throw new DOMException('Session changed', 'AbortError');
    if (authFailed) {
      saveSession(emptySession());
      throw new DOMException('Session expired', 'AbortError');
    }
    return response;
  };

  const loadRecaptcha = (sitekey) => {
    if (!sitekey) return Promise.reject(new Error('Security verification is not configured. Please contact support.'));
    if (captchaLoaders.has(sitekey)) return captchaLoaders.get(sitekey);
    const loader = new Promise((resolve, reject) => {
      let script = Array.from(document.scripts).find((item) => {
        if (!item.src) return false;
        const url = new URL(item.src);
        return ['www.google.com', 'www.recaptcha.net'].includes(url.hostname) && url.pathname === '/recaptcha/api.js' && url.searchParams.get('render') === 'explicit';
      });
      const ownsScript = !script;
      let settled = false;
      const cleanup = () => {
        clearTimeout(timeout);
        script?.removeEventListener('load', ready);
        script?.removeEventListener('error', fail);
      };
      const ready = () => {
        if (!window.grecaptcha?.ready || settled) return;
        window.grecaptcha.ready(() => {
          if (!window.grecaptcha?.render || settled) return;
          settled = true;
          cleanup();
          resolve(window.grecaptcha);
        });
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        cleanup();
        if (ownsScript) script?.remove();
        reject(new Error('Security verification could not load. Please try again.'));
      };
      const timeout = setTimeout(fail, 15000);
      if (ownsScript) {
        script = document.createElement('script');
        const url = new URL('https://www.recaptcha.net/recaptcha/api.js');
        url.searchParams.set('render', 'explicit');
        script.src = url.href;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', ready, { once: true });
        script.addEventListener('error', fail, { once: true });
        document.head.append(script);
      } else {
        script.addEventListener('load', ready, { once: true });
        script.addEventListener('error', fail, { once: true });
        ready();
      }
    }).catch((error) => {
      captchaLoaders.delete(sitekey);
      throw error;
    });
    captchaLoaders.set(sitekey, loader);
    return loader;
  };

  // Stop waiting for the shared script when the dialog closes.
  const waitForCaptcha = (promise, signal) => new Promise((resolve, reject) => {
    const abort = () => reject(signal.reason);
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
    Promise.resolve(promise).then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });

  class AllianceLoginDialog {
    constructor(template) {
      this.template = template;
      this.root = template.content.firstElementChild.cloneNode(true);
      document.body.append(this.root);
      this.form = this.root.querySelector('[data-ua-auth-form]');
      this.email = this.form.elements.email;
      this.password = this.form.elements.password;
      this.toggle = this.root.querySelector('[data-ua-auth-password-toggle]');
      this.captchaContainer = this.root.querySelector('[data-ua-auth-captcha]');
      this.captchaWidgetId = null;
      this.captchaStatus = this.root.querySelector('[data-ua-auth-captcha-status]');
      this.error = this.root.querySelector('[data-ua-auth-error]');
      this.submitButton = this.root.querySelector('[data-ua-auth-submit]');
      this.busy = false;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.form.addEventListener('submit', (event) => this.login(event), options);
      this.root.querySelector('[data-ua-auth-close]').addEventListener('click', () => this.close(), options);
      this.root.addEventListener('click', (event) => { if (event.target === this.root) this.close(); }, options);
      this.toggle.addEventListener('click', () => this.setPasswordVisible(this.password.type === 'password'), options);
      document.addEventListener('keydown', (event) => this.handleKeydown(event), options);
    }

    open(trigger) {
      if (!this.root.hidden) return;
      this.trigger = trigger || document.activeElement;
      this.root.hidden = false;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('userAlliance-auth-open');
      this.setError('');
      this.setPasswordVisible(false);
      this.email.focus({ preventScroll: true });
      this.prepareCaptcha();
    }

    close() {
      if (this.root.hidden) return;
      this.preloadController?.abort();
      this.requestController?.abort();
      this.requestController = null;
      this.resetCaptcha();
      this.password.value = '';
      this.setPasswordVisible(false);
      this.root.hidden = true;
      document.body.style.overflow = this.previousOverflow;
      document.body.classList.remove('userAlliance-auth-open');
      this.setCaptchaStatus('');
      this.setBusy(false);
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    destroy() {
      this.close();
      this.listeners.abort();
      this.root.remove();
    }

    handleKeydown(event) {
      if (this.root.hidden || !this.root.contains(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(this.root.querySelectorAll('button:not(:disabled), input, a[href], iframe, [tabindex="0"]')).filter((item) => item.getClientRects().length);
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && event.target === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && event.target === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    setPasswordVisible(visible) {
      this.password.type = visible ? 'text' : 'password';
      this.toggle.setAttribute('aria-pressed', String(visible));
      this.toggle.setAttribute('aria-label', visible ? 'Hide password' : 'Show password');
      this.toggle.title = visible ? 'Hide password' : 'Show password';
    }

    async prepareCaptcha() {
      if (this.captchaWidgetId !== null) return;
      this.preloadController?.abort();
      const controller = new AbortController();
      this.preloadController = controller;
      this.setCaptchaStatus('Loading security verification...');
      try {
        const captcha = await waitForCaptcha(loadRecaptcha(this.template.dataset.sitekey.trim()), controller.signal);
        controller.signal.throwIfAborted();
        this.captchaWidgetId = captcha.render(this.captchaContainer, {
          sitekey: this.template.dataset.sitekey.trim(),
          size: this.captchaContainer.clientWidth < 304 ? 'compact' : 'normal',
          callback: () => this.setError(''),
          'expired-callback': () => this.setError('Security verification expired. Please verify again.'),
          'error-callback': () => this.setError('Security verification failed. Please try again.')
        });
      } catch (error) {
        if (!controller.signal.aborted && !this.busy) this.setError(error.message);
      } finally {
        if (!controller.signal.aborted && !this.busy) this.setCaptchaStatus('');
      }
    }

    resetCaptcha() {
      if (this.captchaWidgetId !== null) window.grecaptcha.reset(this.captchaWidgetId);
    }

    async login(event) {
      event.preventDefault();
      if (this.root.hidden || this.busy || !this.form.reportValidity()) return;
      this.setError('');
      const storeCode = String(this.template.dataset.storeCode || '').trim().toUpperCase();
      if (!storeCode) {
        this.setError('Store code is missing. Please refresh the page and try again.');
        return;
      }
      const recaptchaResponse = this.captchaWidgetId === null ? '' : window.grecaptcha.getResponse(this.captchaWidgetId);
      if (!recaptchaResponse) {
        this.setError('Please complete the security verification.');
        if (this.captchaWidgetId === null) this.prepareCaptcha();
        return;
      }
      const controller = new AbortController();
      this.requestController = controller;
      const timeout = setTimeout(() => controller.abort(), 30000);
      const email = this.email.value.trim();
      console.log(this.template.dataset.storeCode,'*****************storeCode');
      this.setBusy(true);
      try {
        const response = await fetch(LOGIN_ENDPOINT, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeCode: storeCode === 'GB' ? 'UK' : storeCode, email, password: this.password.value, recaptchaResponse }),
          signal: controller.signal
        });
        const payload = await response.json();
        if (controller.signal.aborted) return;
        if (!response.ok || payload?.success === false || (payload?.code !== undefined && Number(payload.code) !== 0)) {
          const message = payload?.message || payload?.msg;
          throw new Error(typeof message === 'string' ? message : 'Login failed. Please check your email and password.');
        }
        const data = payload?.data ?? payload;
        const sessionData = data?.data && typeof data.data === 'object' ? data.data : data;
        if (typeof sessionData?.accessToken !== 'string' || !sessionData.accessToken.trim()) throw new Error('Login did not return an access token. Please contact support.');
        saveSession({
          storeCode: sessionData.storeCode || storeCode,
          email: sessionData.email || email,
          accessToken: sessionData.accessToken,
          affiliateId: sessionData.affiliateId
        });
        this.close();
      } catch (error) {
        if (this.root.hidden || this.requestController !== controller) return;
        this.resetCaptcha();
        this.setError(error.name === 'AbortError' ? 'Login timed out. Please try again.' : error instanceof TypeError || error instanceof SyntaxError ? 'Unable to sign in. Please try again.' : error.message);
      } finally {
        clearTimeout(timeout);
        if (this.requestController === controller) {
          this.requestController = null;
          this.setCaptchaStatus('');
          this.setBusy(false);
        }
      }
    }

    setBusy(busy, label = 'Signing in...') {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.email.readOnly = busy;
      this.password.readOnly = busy;
      this.submitButton.disabled = busy;
      this.submitButton.textContent = busy ? label : 'Login';
    }

    setCaptchaStatus(message) {
      if (!this.captchaStatus) return;
      this.captchaStatus.textContent = message;
      this.captchaStatus.hidden = !message;
    }

    setError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }

  const open = (trigger) => {
    const localSection = trigger?.closest('.shopify-section');
    const template = document.querySelector('[data-ua-auth-primary]') || localSection?.querySelector('[data-ua-auth-template]') || document.querySelector('[data-ua-auth-template]');
    if (!template) return;
    if (activeDialog?.template !== template) {
      activeDialog?.destroy();
      activeDialog = new AllianceLoginDialog(template);
    }
    activeDialog.open(trigger);
  };

  window.UserAllianceAuth = { getSession, isAuthenticated, open, request, clearSession: () => saveSession(emptySession()) };
  Object.defineProperty(window.UserAllianceAuth, 'session', { get: getSession });
  syncSession(session);
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    try { syncSession(JSON.parse(event.newValue)); } catch (error) { syncSession(emptySession()); }
    if (isAuthenticated()) activeDialog?.close();
  });
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-ua-auth-trigger], user-alliance-share .userAlliance-share__social-link, user-alliance-share [data-ua-share-button]');
    if (!trigger || isAuthenticated()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    open(trigger);
  }, true);
  document.addEventListener('shopify:section:unload', (event) => {
    if (!activeDialog || !event.target.contains(activeDialog.template)) return;
    activeDialog.destroy();
    activeDialog = null;
  });
})();
